import React, { useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Image,
  KeyboardAvoidingView,
  Platform,
  FlatList,
  ActivityIndicator,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon as CustomIcon, DeleteModal, Skeleton, GroupAvatar } from '@components'
import ScheduleCalendarModal from './components/ScheduleCalendarModal'
import dayjs from 'dayjs'
import { Theme } from '@theme'
import useComposeMessageCtrl from './useComposeMessageCtrl'
import {
  useCreateScheduledMessage,
  useCreationStackIntegration,
  useDeleteScheduledMessage,
  useGetScheduledMessageById,
  usePaginateScheduledMessagesByRecipientId,
  useUpdateScheduledMessage,
} from '@hooks'
import { useSafeNavigation } from '../../../navigation/web/useSafeNavigation'
import { SafeAreaView } from 'react-native-safe-area-context'
import DefaultAvatar from './components/DefaultAvatar'
import { Scope } from '@utils'
import { useLanguage } from '@context'

interface ComposeMessageProps {
  isEdit?: boolean
  scheduledMessageId?: string
  stackData?: Record<string, any>
}

const ComposeMessage: React.FC<ComposeMessageProps> = ({
  isEdit: propIsEdit,
  scheduledMessageId: propScheduledMessageId,
  stackData,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const navigation = useSafeNavigation()
  const { nextStep, previousStep, updateStackData, currentStack, isVisible } =
    useCreationStackIntegration()
  const { goBack, getRouteParams } = useComposeMessageCtrl({
    onBack: previousStep,
    onNext: nextStep,
    updateStackData: updateStackData,
  })

  // Get params from route, props, or stack data
  const routeParams = getRouteParams()
  const isEditProp = propIsEdit ?? routeParams.isEdit ?? currentStack?.data?.isEdit ?? false
  const [scheduledMessageId, setScheduledMessageId] = useState(
    propScheduledMessageId ??
      routeParams.scheduledMessageId ??
      currentStack?.data?.scheduledMessageId ??
      stackData?.scheduledMessageId,
  )

  const { data: scheduledMessage } = useGetScheduledMessageById(scheduledMessageId || '')
  const { flatData: scheduledMessages, refetch: refetchScheduledMessages } =
    usePaginateScheduledMessagesByRecipientId(scheduledMessage?.recipientAccountId || '', {
      groupId: scheduledMessage?.groupId || undefined,
    })
  const { mutateAsync: deleteScheduledMessage } = useDeleteScheduledMessage()
  const { mutateAsync: updateScheduledMessage } = useUpdateScheduledMessage()
  const { mutateAsync: addScheduledMessage } = useCreateScheduledMessage()

  const [isLoading, setIsLoading] = useState(false)
  const [isCalendarVisible, setIsCalendarVisible] = useState(false)
  const [message, setMessage] = useState('')
  const [date, setDate] = useState<string>(dayjs().format('DD-MM-YYYY'))
  const [time, setTime] = useState<string>(dayjs().add(1, 'hour').format('HH:mm'))
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [messageIdToDelete, setMessageIdToDelete] = useState<string | null>(null)
  const [isEdit, setIsEdit] = useState(isEditProp)

  useEffect(() => {
    if (isEdit && scheduledMessage) {
      setMessage(scheduledMessage.message)
      setDate(scheduledMessage.date)
      setTime(scheduledMessage.time)
    }
  }, [isEdit, scheduledMessage])

  const handleBack = () => {
    if (isVisible) {
      goBack()
    } else {
      navigation?.goBack()
    }
  }

  const handleSendMessage = () => {
    if (message.trim() && scheduledMessage) {
      setIsLoading(true)
      if (isEdit) {
        updateScheduledMessage({
          id: scheduledMessage.id,
          message,
          scheduledDate: dayjs(date, 'DD-MM-YYYY')
            .hour(parseInt(time.split(':')[0]))
            .minute(parseInt(time.split(':')[1]))
            .toISOString(),
          recipientAccountId: scheduledMessage.recipientAccountId ?? undefined,
          groupId: scheduledMessage.groupId ?? undefined,
        })
          .then(() => {
            refetchScheduledMessages()
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        addScheduledMessage({
          message,
          scheduledDate: dayjs(date, 'DD-MM-YYYY')
            .hour(parseInt(time.split(':')[0]))
            .minute(parseInt(time.split(':')[1]))
            .toISOString(),
          recipientAccountId: scheduledMessage.recipientAccountId ?? undefined,
          groupId: scheduledMessage.groupId ?? undefined,
        })
          .then(() => {
            refetchScheduledMessages()
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    }
  }

  const showDeleteModal = (messageId: string) => {
    setIsDeleteModalVisible(true)
    setMessageIdToDelete(messageId)
  }

  const handleDeleteMessage = () => {
    if (messageIdToDelete) {
      // When the message being viewed in the detail panel is the one deleted,
      // clear the selected id (so useGetScheduledMessageById is disabled and no
      // GET /scheduled-messages/:id fires for the deleted id) and close the panel.
      const wasCurrentMessage = messageIdToDelete === scheduledMessageId
      deleteScheduledMessage({ id: messageIdToDelete })
        .then(() => {
          if (wasCurrentMessage) {
            setScheduledMessageId(undefined)
            handleBack()
          } else {
            refetchScheduledMessages()
          }
        })
        .finally(() => {
          setIsDeleteModalVisible(false)
          setMessageIdToDelete(null)
        })
    }
  }

  const beforeEditMessage = (messageId: string) => {
    setScheduledMessageId(messageId)
    setIsEdit(true)
  }

  const formatDate = (date: string) => {
    const isToday = dayjs(date, 'DD-MM-YYYY').isSame(dayjs(), 'day')
    const isTomorrow = dayjs(date, 'DD-MM-YYYY').isSame(dayjs().add(1, 'day'), 'day')
    if (isToday) return t('common.today')
    if (isTomorrow) return t('common.tomorrow')
    return dayjs(date, 'DD-MM-YYYY').format('DD MMM YYYY')
  }

  // After all hooks are declared, it's safe to conditionally render.
  if (!scheduledMessage) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <CustomIcon name="arrowBack" size={20} color="#000000" />
          </TouchableOpacity>
          <Skeleton style={styles.skeletonTitle} height={16} width={140} borderRadius={8} />
        </View>
        <View style={styles.skeletonBody}>
          <View style={styles.skeletonRow}>
            <Skeleton style={styles.skeletonAvatar} height={36} width={36} borderRadius={18} />
            <View style={styles.skeletonTextCol}>
              <Skeleton style={styles.skeletonLine} height={12} width="100%" borderRadius={6} />
              <Skeleton style={styles.skeletonLineShort} height={12} width="60%" borderRadius={6} />
            </View>
          </View>
          <View style={styles.skeletonMessageBlock}>
            <Skeleton style={styles.skeletonLine} height={12} width="100%" borderRadius={6} />
            <Skeleton style={styles.skeletonLine} height={12} width="100%" borderRadius={6} />
            <Skeleton style={styles.skeletonLineShort} height={12} width="60%" borderRadius={6} />
          </View>
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {!isVisible && (
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <CustomIcon name="arrowBack" size={20} color="#000000" />
          </TouchableOpacity>
        )}

        <View style={styles.contactInfo}>
          {scheduledMessage.scope === Scope.GROUP && scheduledMessage.groupParticipants?.length ? (
            <GroupAvatar profiles={scheduledMessage.groupParticipants} />
          ) : scheduledMessage.recipient?.photo ? (
            <Image
              source={{ uri: scheduledMessage.recipient.photo }}
              style={styles.contactAvatar}
            />
          ) : (
            <DefaultAvatar
              name={scheduledMessage.recipient?.name || ''}
              scope={
                scheduledMessage.scope as
                  | Scope.PRIVATE
                  | Scope.PROFESSIONAL
                  | Scope.GROUP
                  | Scope.CATEGORY
                  | Scope.HASHTAG
              }
            />
          )}
          <View style={styles.contactDetails}>
            <Text style={styles.contactName}>{scheduledMessage.recipient?.name}</Text>
          </View>
        </View>

        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerAction}>
            <CustomIcon name="translate" size={20} color="#000000" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerAction}>
            <CustomIcon name="moreVertical" size={20} color="#007AFF" />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.content}>
        {/* Zone de conversation avec message programmé */}
        <View style={styles.conversationArea}>
          <FlatList
            data={scheduledMessages}
            renderItem={({ item: scheduledMessage }) => (
              <View key={scheduledMessage.id}>
                {/* Séparateur de date */}
                <View style={styles.dateSection}>
                  <View style={styles.dateLine} />
                  <Text style={styles.dateText}>{formatDate(scheduledMessage.date)}</Text>
                  <View style={styles.dateLine} />
                </View>

                {/* Bulle de message programmé */}
                <View style={styles.messageContainer}>
                  <View style={styles.messageBubble}>
                    <Text style={styles.messageBubbleText}>{scheduledMessage.message}</Text>
                  </View>
                  <TouchableOpacity
                    style={styles.deleteMessageButton}
                    onPress={() => showDeleteModal(scheduledMessage.id)}
                  >
                    <CustomIcon name="trash" size={16} color="#FF3B30" />
                  </TouchableOpacity>
                </View>

                {/* Info de programmation — toute la ligne est cliquable pour
                    modifier (cible bien plus grande que l'ancien crayon seul). */}
                <TouchableOpacity
                  style={styles.scheduleInfo}
                  activeOpacity={0.7}
                  onPress={() => beforeEditMessage(scheduledMessage.id)}
                >
                  <Text style={styles.scheduleInfoText}>
                    {t('scheduledMessagesScreen.scheduledFor', { time: scheduledMessage.time })}
                  </Text>
                  <View style={styles.editScheduleButton}>
                    <CustomIcon name="edit" size={16} color={theme.colors.primary} />
                    <Text style={styles.editScheduleLabel}>{t('common.edit')}</Text>
                  </View>
                </TouchableOpacity>
              </View>
            )}
          />
        </View>
      </View>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.footer}>
          {isEdit && (
            <View style={styles.editingBanner}>
              <CustomIcon name="edit" size={14} color={theme.colors.primary} />
              <Text style={styles.editingBannerText}>
                {t('scheduledMessagesScreen.editing')}
              </Text>
              <TouchableOpacity
                style={styles.editingBannerClose}
                onPress={() => {
                  setIsEdit(false)
                  setMessage('')
                }}
              >
                <CustomIcon name="cross" size={14} color={theme.colors.greyBlue} />
              </TouchableOpacity>
            </View>
          )}
          <View style={styles.inputRow}>
            <TouchableOpacity
              style={styles.calendarButton}
              onPress={() => setIsCalendarVisible(true)}
            >
              <CustomIcon name="calendar" size={20} color={theme.colors.green} />
            </TouchableOpacity>
            <View style={styles.messageInputContainer}>
              <TextInput
                style={styles.messageInput}
                placeholder={t('scheduledMessagesScreen.typeMessage')}
                placeholderTextColor="#8E8E93"
                value={message}
                onChangeText={setMessage}
              />
              <TouchableOpacity
                style={styles.sendButton}
                onPress={handleSendMessage}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <CustomIcon name="send" size={18} color="white" />
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
      <ScheduleCalendarModal
        isVisible={isCalendarVisible}
        setIsVisible={setIsCalendarVisible}
        date={date}
        time={time}
        onDateSelect={setDate}
        onTimeChange={setTime}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title={t('scheduledMessagesScreen.deleteConfirm')}
        onDelete={handleDeleteMessage}
      />
    </SafeAreaView>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#F2F2F7',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: 'white',
      borderBottomWidth: 0.5,
      borderBottomColor: '#C7C7CC',
    },
    backButton: {
      padding: 8,
    },
    contactInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginHorizontal: 16,
    },
    contactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      marginRight: 12,
    },
    contactDetails: {
      flex: 1,
    },
    contactName: {
      fontSize: 16,
      fontWeight: '600',
      color: '#000',
    },
    skeletonTitle: {
      height: 16,
      width: 140,
      borderRadius: 8,
      backgroundColor: '#E5E5EA',
    },
    skeletonBody: {
      paddingHorizontal: 16,
      paddingTop: 16,
    },
    skeletonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      backgroundColor: 'white',
      borderRadius: 12,
      paddingHorizontal: 12,
    },
    skeletonAvatar: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: '#E5E5EA',
      marginRight: 12,
    },
    skeletonTextCol: {
      flex: 1,
    },
    skeletonLine: {
      height: 12,
      borderRadius: 6,
      backgroundColor: '#E5E5EA',
      marginBottom: 8,
      width: '100%',
    },
    skeletonLineShort: {
      height: 12,
      borderRadius: 6,
      backgroundColor: '#E5E5EA',
      width: '60%',
    },
    skeletonMessageBlock: {
      marginTop: 16,
      padding: 12,
      backgroundColor: 'white',
      borderRadius: 12,
    },
    contactStatus: {
      fontSize: 13,
      color: '#8E8E93',
      marginTop: 2,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerAction: {
      padding: 6,
      marginLeft: 2,
    },
    content: {
      flex: 1,
    },
    conversationArea: {
      flex: 1,
      paddingHorizontal: 16,
      paddingTop: 20,
    },
    dateSection: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    dateLine: {
      flex: 1,
      height: 1,
      backgroundColor: theme.colors.grey10,
    },
    dateText: {
      paddingHorizontal: 12,
      fontSize: 12,
      color: '#007AFF',
      fontWeight: '500',
    },
    messageContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginBottom: 8,
    },
    messageBubble: {
      backgroundColor: theme.colors.primary,
      borderRadius: 26,
      paddingHorizontal: 20,
      paddingVertical: 12,
      maxWidth: '70%',
    },
    messageBubbleText: {
      fontSize: 16,
      color: 'white',
    },
    deleteMessageButton: {
      marginLeft: 12,
      padding: 6,
    },
    scheduleInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      marginBottom: 4,
      paddingVertical: 6,
    },
    scheduleInfoText: {
      fontSize: 12,
      color: '#8E8E93',
      fontStyle: 'italic',
    },
    editScheduleButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 4,
      paddingHorizontal: 10,
      borderRadius: 14,
      backgroundColor: theme.colors.primary + '14',
    },
    editScheduleLabel: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.colors.primary,
    },
    editingBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      backgroundColor: theme.colors.primary + '14',
      borderRadius: 12,
      paddingVertical: 8,
      paddingHorizontal: 12,
      marginBottom: 10,
    },
    editingBannerText: {
      flex: 1,
      fontSize: 13,
      fontWeight: '600',
      color: theme.colors.primary,
    },
    editingBannerClose: {
      padding: 2,
    },
    footer: {
      backgroundColor: 'white',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 0.5,
      borderTopColor: '#C7C7CC',
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    messageInputContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#F2F2F7',
      borderRadius: 25,
      paddingRight: 5,
      paddingLeft: 16,
      paddingVertical: 5,
    },
    messageInput: {
      flex: 1,
      fontSize: 16,
      color: '#000',
      paddingVertical: 0,
    },
    sendButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 20,
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 4,
    },
    calendarButton: {
      backgroundColor: theme.colors.green + '1A',
      borderWidth: 1.5,
      borderColor: theme.colors.green,
      borderRadius: 22,
      width: 44,
      height: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      justifyContent: 'flex-end',
    },
    overlayTouchable: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    modalContainer: {
      backgroundColor: 'white',
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      minHeight: 200,
    },
    handle: {
      width: 36,
      height: 4,
      backgroundColor: '#C7C7CC',
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 8,
      marginBottom: 8,
    },
    // Styles pour le calendrier
    calendarContainer: {
      padding: 20,
    },
    monthNavigation: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 30,
    },
    monthText: {
      fontSize: 18,
      fontWeight: '600',
      color: '#000',
      marginHorizontal: 20,
    },
    weekDaysContainer: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      marginBottom: 20,
    },
    weekDayText: {
      fontSize: 13,
      color: '#8E8E93',
      fontWeight: '500',
      width: 40,
      textAlign: 'center',
    },
    calendarGrid: {
      marginBottom: 30,
    },
    calendarRow: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      marginBottom: 15,
    },
    calendarDay: {
      fontSize: 16,
      color: '#000',
      width: 40,
      height: 40,
      textAlign: 'center',
      textAlignVertical: 'center',
      lineHeight: 40,
    },
    selectedDayContainer: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: '#007AFF',
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectedDayText: {
      fontSize: 16,
      color: 'white',
      fontWeight: '600',
    },
    timeSelector: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      marginBottom: 30,
    },
    timeLabel: {
      fontSize: 16,
      color: '#000',
      fontWeight: '500',
    },
    timeContainer: {
      backgroundColor: '#F2F2F7',
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 8,
    },
    timeText: {
      fontSize: 16,
      color: '#000',
      fontWeight: '500',
    },
    scheduleButtonContainer: {
      paddingHorizontal: 20,
      marginBottom: 20,
    },
    scheduleButton: {
      backgroundColor: '#007AFF',
      borderRadius: 25,
      paddingVertical: 16,
      alignItems: 'center',
    },
    scheduleButtonText: {
      fontSize: 17,
      fontWeight: '600',
      color: 'white',
    },
    safeArea: {
      flex: 1,
    },
  })

export default ComposeMessage
